import React from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Box,
} from '@mui/material'

interface UserData {
  id: number
  sub: string
  email: string
  username: string | null
  subscription: {
    planName: string
    status: string
    startDate: string
    endDate: string | null
  } | null
  usage: {
    month: string
    aiCreditsUsed: number
    itemsCreated: number
  } | null
}

interface UsersTableProps {
  users: UserData[]
}

const statusColors: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  ACTIVE: 'success',
  FREE: 'default',
  CANCELED: 'warning',
  EXPIRED: 'error',
}

export default function UsersTable({ users }: UsersTableProps): JSX.Element {
  return (
    <TableContainer
      sx={{
        background: 'rgba(15, 23, 42, 0.8)',
        backdropFilter: 'blur(24px)',
        border: '1px solid rgba(168, 85, 247, 0.2)',
        borderRadius: '16px',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ padding: '20px 24px 8px' }}>
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Users ({users.length})
        </Typography>
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            {['Email', 'Username', 'Plan', 'Status', 'AI Credits Used', 'Items Created'].map(
              header => (
                <TableCell
                  key={header}
                  sx={{
                    color: 'rgba(255,255,255,0.5)',
                    borderBottom: '1px solid rgba(168, 85, 247, 0.15)',
                    fontSize: '12px',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {header}
                </TableCell>
              )
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map(user => (
            <TableRow
              key={user.id}
              sx={{
                '&:hover': { background: 'rgba(168, 85, 247, 0.05)' },
              }}
            >
              <TableCell
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                }}
              >
                {user.email}
              </TableCell>
              <TableCell
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                }}
              >
                {user.username ?? '-'}
              </TableCell>
              <TableCell
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                }}
              >
                {user.subscription?.planName ?? 'FREE'}
              </TableCell>
              <TableCell
                sx={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
              >
                <Chip
                  label={user.subscription?.status ?? 'FREE'}
                  color={statusColors[user.subscription?.status ?? 'FREE'] ?? 'default'}
                  size="small"
                  sx={{ fontSize: '11px', height: '22px' }}
                />
              </TableCell>
              <TableCell
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                }}
              >
                {user.usage?.aiCreditsUsed ?? 0}
              </TableCell>
              <TableCell
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                }}
              >
                {user.usage?.itemsCreated ?? 0}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
